import { http } from './http';
import type {
  AssignDeliveryNoteWorksiteRequest,
  AssignDeliveryNoteItemRequest,
  ConfirmMaterialRequest,
  CreateDeliveryNoteIntakeTokenRequest,
  CreateDeliveryNoteItemRequest,
  CreateDeliveryNoteRequest,
  CreateMaterialFromDdtItemRequest,
  CreateMaterialFromDdtItemResponse,
  DeliveryNoteDto,
  DeliveryNoteIntakeTokenCreatedDto,
  DeliveryNoteIntakeTokenListItemDto,
  DeliveryNoteItemDto,
  DeliveryNoteItemAssignmentDto,
  DeliveryNoteOcrExtractedDto,
  DeliveryNoteOcrLogEntryDto,
  DeliveryNoteOcrUsageDto,
  DeliveryNoteSummaryDto,
  LoadInventoryFromDdtBatchRequest,
  PageResponse,
  SupplierMaterialMappingSuggestionDto,
  UpdateDeliveryNoteItemRequest,
  UpdateDeliveryNoteRequest,
} from './types';
import { filesApi } from './files';

export const deliveryNotesApi = {
  create(data: CreateDeliveryNoteRequest): Promise<DeliveryNoteDto> {
    return http.post<DeliveryNoteDto>('/delivery-notes', data).then((r) => r.data);
  },

  get(id: string): Promise<DeliveryNoteDto> {
    return http.get<DeliveryNoteDto>(`/delivery-notes/${id}`).then((r) => r.data);
  },

  /** File intake/OCR su blob storage (GET autenticato). */
  downloadOriginalFile(deliveryNoteId: string, signal?: AbortSignal): Promise<Blob> {
    return http
      .get<Blob>(`/delivery-notes/${deliveryNoteId}/original-file`, {
        responseType: 'blob',
        signal,
      })
      .then((r) => r.data);
  },

  update(id: string, data: UpdateDeliveryNoteRequest): Promise<DeliveryNoteDto> {
    return http.put<DeliveryNoteDto>(`/delivery-notes/${id}`, data).then((r) => r.data);
  },

  delete(id: string): Promise<void> {
    return http.delete(`/delivery-notes/${id}`).then(() => undefined);
  },

  /** R23-MAG2: lista globale (Admin/Resp). */
  listGlobal(
    params: {
      page?: number;
      size?: number;
      sort?: string;
      status?: string;
      supplierId?: string;
      worksiteId?: string;
      unassignedWorksite?: boolean;
      dateFrom?: string;
      dateTo?: string;
    } = {},
  ): Promise<PageResponse<DeliveryNoteSummaryDto>> {
    return http
      .get<PageResponse<DeliveryNoteSummaryDto>>('/delivery-notes', {
        params: {
          page: 0,
          size: 20,
          sort: 'documentDate,desc',
          ...params,
        },
      })
      .then((r) => r.data);
  },

  loadInventoryBatch(deliveryNoteId: string, body: LoadInventoryFromDdtBatchRequest): Promise<void> {
    return http
      .post(`/delivery-notes/${deliveryNoteId}/load-inventory`, body)
      .then(() => undefined);
  },

  createMaterialFromItem(
    itemId: string,
    body: CreateMaterialFromDdtItemRequest,
  ): Promise<CreateMaterialFromDdtItemResponse> {
    return http
      .post<CreateMaterialFromDdtItemResponse>(`/delivery-notes/items/${itemId}/create-material`, body)
      .then((r) => r.data);
  },

  listByWorksite(
    worksiteId: string,
    params: { page?: number; size?: number } = {},
  ): Promise<PageResponse<DeliveryNoteSummaryDto>> {
    return http
      .get<PageResponse<DeliveryNoteSummaryDto>>(`/worksites/${worksiteId}/delivery-notes`, {
        params: { page: 0, size: 20, ...params },
      })
      .then((r) => r.data);
  },

  listByReport(
    reportId: string,
    params: { page?: number; size?: number } = {},
  ): Promise<PageResponse<DeliveryNoteSummaryDto>> {
    return http
      .get<PageResponse<DeliveryNoteSummaryDto>>(`/reports/${reportId}/delivery-notes`, {
        params: { page: 0, size: 20, ...params },
      })
      .then((r) => r.data);
  },

  attachPhoto(id: string, fileAssetId: string): Promise<void> {
    return http.post(`/delivery-notes/${id}/photo`, { fileAssetId }).then(() => undefined);
  },

  removePhoto(id: string): Promise<void> {
    return http.delete(`/delivery-notes/${id}/photo`).then(() => undefined);
  },

  /**
   * Upload a file via /files then attach it as the delivery note photo.
   * Caller should pass a File from input or camera.
   */
  async uploadDeliveryNotePhoto(deliveryNoteId: string, file: File): Promise<void> {
    const { fileAssetId } = await filesApi.upload(file);
    await deliveryNotesApi.attachPhoto(deliveryNoteId, fileAssetId);
  },

  // ─── R14.3.2 Delivery note items (smart matching) ───────────────────────────

  createDeliveryNoteItem(
    deliveryNoteId: string,
    data: CreateDeliveryNoteItemRequest,
  ): Promise<DeliveryNoteItemDto> {
    return http
      .post<DeliveryNoteItemDto>(`/delivery-notes/${deliveryNoteId}/items`, data)
      .then((r) => r.data);
  },

  listDeliveryNoteItems(
    deliveryNoteId: string,
    params: { page?: number; size?: number } = {},
  ): Promise<PageResponse<DeliveryNoteItemDto>> {
    return http
      .get<PageResponse<DeliveryNoteItemDto>>(`/delivery-notes/${deliveryNoteId}/items`, {
        params: { page: 0, size: 500, ...params },
      })
      .then((r) => r.data);
  },

  getDeliveryNoteItem(id: string): Promise<DeliveryNoteItemDto> {
    return http.get<DeliveryNoteItemDto>(`/delivery-note-items/${id}`).then((r) => r.data);
  },

  assignDeliveryNoteItemToWorksite(
    deliveryNoteId: string,
    itemId: string,
    data: AssignDeliveryNoteItemRequest,
  ): Promise<void> {
    return http
      .post(`/delivery-notes/${deliveryNoteId}/items/${itemId}/assign`, data)
      .then(() => undefined);
  },

  removeDeliveryNoteItemAssignment(
    deliveryNoteId: string,
    itemId: string,
    worksiteId: string,
  ): Promise<void> {
    return http
      .delete(`/delivery-notes/${deliveryNoteId}/items/${itemId}/assign/${worksiteId}`)
      .then(() => undefined);
  },

  listDeliveryNoteItemAssignments(
    deliveryNoteId: string,
    itemId: string,
  ): Promise<DeliveryNoteItemAssignmentDto[]> {
    return http
      .get<DeliveryNoteItemAssignmentDto[]>(`/delivery-notes/${deliveryNoteId}/items/${itemId}/assignments`)
      .then((r) => r.data);
  },

  updateDeliveryNoteItem(
    id: string,
    data: UpdateDeliveryNoteItemRequest,
  ): Promise<DeliveryNoteItemDto> {
    return http.put<DeliveryNoteItemDto>(`/delivery-note-items/${id}`, data).then((r) => r.data);
  },

  deleteDeliveryNoteItem(id: string): Promise<void> {
    return http.delete(`/delivery-note-items/${id}`).then(() => undefined);
  },

  getDeliveryNoteItemSuggestion(
    itemId: string,
  ): Promise<SupplierMaterialMappingSuggestionDto> {
    return http
      .get<SupplierMaterialMappingSuggestionDto>(`/delivery-note-items/${itemId}/suggestion`)
      .then((r) => r.data);
  },

  /** Suggestion for a new item (before save), by supplierDescription and unit. */
  getDeliveryNoteSuggestion(
    deliveryNoteId: string,
    params: { supplierDescription: string; unit?: string },
  ): Promise<SupplierMaterialMappingSuggestionDto> {
    return http
      .get<SupplierMaterialMappingSuggestionDto>('/delivery-notes/suggestion', {
        params: {
          deliveryNoteId,
          supplierDescription: params.supplierDescription,
          ...(params.unit != null && params.unit !== '' && { unit: params.unit }),
        },
      })
      .then((r) => r.data);
  },

  confirmDeliveryNoteItemMaterial(
    itemId: string,
    data: ConfirmMaterialRequest,
  ): Promise<DeliveryNoteItemDto> {
    return http
      .post<DeliveryNoteItemDto>(`/delivery-note-items/${itemId}/confirm-material`, data)
      .then((r) => r.data);
  },

  clearDeliveryNoteItemMaterial(itemId: string): Promise<DeliveryNoteItemDto> {
    return http
      .delete<DeliveryNoteItemDto>(`/delivery-note-items/${itemId}/material`)
      .then((r) => r.data);
  },

  getDeliveryNoteItemSuggestions(
    deliveryNoteId: string,
  ): Promise<SupplierMaterialMappingSuggestionDto[]> {
    return http
      .get<SupplierMaterialMappingSuggestionDto[]>(
        `/delivery-notes/${deliveryNoteId}/item-suggestions`,
      )
      .then((r) => r.data);
  },

  /** R22.1 OCR; R22.2: opzionale deliveryNoteId per salvare blob su bolla */
  processOcr(file: File, deliveryNoteId?: string | null): Promise<DeliveryNoteOcrExtractedDto> {
    const fd = new FormData();
    fd.append('file', file);
    return http
      .post<DeliveryNoteOcrExtractedDto>('/delivery-notes/ocr', fd, {
        params:
          deliveryNoteId != null && deliveryNoteId !== ''
            ? { deliveryNoteId }
            : undefined,
      })
      .then((r) => r.data);
  },

  /** R22.1-FIX6: più foto (max 5) JPEG/PNG — un solo prompt LLM */
  processOcrMulti(files: File[], deliveryNoteId?: string | null): Promise<DeliveryNoteOcrExtractedDto> {
    const fd = new FormData();
    for (const f of files) {
      fd.append('files', f);
    }
    return http
      .post<DeliveryNoteOcrExtractedDto>('/delivery-notes/ocr/multi', fd, {
        params:
          deliveryNoteId != null && deliveryNoteId !== ''
            ? { deliveryNoteId }
            : undefined,
      })
      .then((r) => r.data);
  },

  /** R22.2: analizza file già salvato (draft intake / upload con deliveryNoteId) */
  analyzeOcr(deliveryNoteId: string): Promise<DeliveryNoteOcrExtractedDto> {
    return http
      .post<DeliveryNoteOcrExtractedDto>(`/delivery-notes/${deliveryNoteId}/analyze-ocr`)
      .then((r) => r.data);
  },

  listPendingAssignment(
    params: { page?: number; size?: number } = {},
  ): Promise<PageResponse<DeliveryNoteSummaryDto>> {
    return http
      .get<PageResponse<DeliveryNoteSummaryDto>>('/delivery-notes/pending-assignment', {
        params: { page: 0, size: 20, ...params },
      })
      .then((r) => r.data);
  },

  countPendingAssignment(): Promise<{ count: number }> {
    return http
      .get<{ count: number }>('/delivery-notes/pending-assignment/count')
      .then((r) => r.data);
  },

  assignWorksite(id: string, body: AssignDeliveryNoteWorksiteRequest): Promise<DeliveryNoteDto> {
    return http.patch<DeliveryNoteDto>(`/delivery-notes/${id}/assign-worksite`, body).then((r) => r.data);
  },

  /** R22.2-FIX1: conferma esplicita bolla intake (DRAFT → CONFIRMED). */
  confirm(id: string): Promise<DeliveryNoteDto> {
    return http.patch<DeliveryNoteDto>(`/delivery-notes/${id}/confirm`).then((r) => r.data);
  },

  createIntakeToken(
    body: CreateDeliveryNoteIntakeTokenRequest,
  ): Promise<DeliveryNoteIntakeTokenCreatedDto> {
    return http
      .post<DeliveryNoteIntakeTokenCreatedDto>('/delivery-notes/intake-tokens', body)
      .then((r) => r.data);
  },

  listIntakeTokens(
    params: { page?: number; size?: number } = {},
  ): Promise<PageResponse<DeliveryNoteIntakeTokenListItemDto>> {
    return http
      .get<PageResponse<DeliveryNoteIntakeTokenListItemDto>>('/delivery-notes/intake-tokens', {
        params: { page: 0, size: 20, ...params },
      })
      .then((r) => r.data);
  },

  revokeIntakeToken(id: string): Promise<void> {
    return http.delete(`/delivery-notes/intake-tokens/${id}`).then(() => undefined);
  },

  confirmOcr(ocrLogId: string, deliveryNoteId: string): Promise<void> {
    return http
      .post(`/delivery-notes/ocr/${ocrLogId}/confirm`, { deliveryNoteId })
      .then(() => undefined);
  },

  getOcrLog(params: { page?: number; size?: number } = {}): Promise<PageResponse<DeliveryNoteOcrLogEntryDto>> {
    return http
      .get<PageResponse<DeliveryNoteOcrLogEntryDto>>('/delivery-notes/ocr/log', {
        params: { page: params.page ?? 0, size: params.size ?? 20 },
      })
      .then((r) => r.data);
  },

  /** R22.1-FIX1: Admin/Resp — monthly billable OCR count */
  getOcrUsage(): Promise<DeliveryNoteOcrUsageDto> {
    return http.get<DeliveryNoteOcrUsageDto>('/delivery-notes/ocr/usage').then((r) => r.data);
  },
};
